import { ScenarioElement } from '@integration-app/sdk'
import { uuid4 } from '@sentry/utils'
import { Edge, Node } from 'reactflow'
import { ScenarioDiagramNodeType } from './nodes'
import {
  SCENARIO_ELEMENT_NODE_HEIGHT,
  SCENARIO_ELEMENT_NODE_WIDTH,
} from './nodes/ScenarioElementNode'
import {
  EXTERNAL_APPS_NODE_TEXT_LABEL_HEIGHT,
  EXTERNAL_APPS_NODE_WIDTH,
  calculateExternalAppsNodeHeight,
} from './nodes/ExternalAppsNode'
import { YOUR_APP_NODE_HEIGHT, YOUR_APP_NODE_WIDTH } from './nodes/YourAppNode'
import { MinimalConnector } from 'routes/Scenarios/helpers/types'

const HORIZONTAL_GAP = 100
const VERTICAL_GAP = 50

function centerRegion(bigger: number, smaller: number): number {
  return bigger / 2 - smaller / 2
}

export function buildNodesAndEdgesFromScenarioElements(
  elements: ScenarioElement[],
  connectors: MinimalConnector[],
): {
  nodes: Node[]
  edges: Edge[]
} {
  const nodes: Node[] = []
  const edges: Edge[] = []

  if (elements.length === 0) {
    return { nodes, edges }
  }

  const n = elements.length

  const scenarioElementsNodesHeight =
    n * (SCENARIO_ELEMENT_NODE_HEIGHT + VERTICAL_GAP) - VERTICAL_GAP

  const yourAppNodeId = uuid4()
  nodes.push({
    id: yourAppNodeId,
    type: ScenarioDiagramNodeType.YourApp,
    data: {},
    width: YOUR_APP_NODE_WIDTH,
    height: YOUR_APP_NODE_HEIGHT,
    position: {
      x: -(SCENARIO_ELEMENT_NODE_WIDTH + HORIZONTAL_GAP),
      y: centerRegion(scenarioElementsNodesHeight, YOUR_APP_NODE_HEIGHT),
    },
  })

  const externalAppsNodeId = uuid4()
  const externalAppsNodeHeight = calculateExternalAppsNodeHeight(
    connectors.length,
  )
  nodes.push({
    id: externalAppsNodeId,
    type: ScenarioDiagramNodeType.ExternalApps,
    data: {
      connectors,
    },
    width: EXTERNAL_APPS_NODE_WIDTH,
    height: externalAppsNodeHeight,
    position: {
      x: SCENARIO_ELEMENT_NODE_WIDTH + HORIZONTAL_GAP,
      y:
        centerRegion(scenarioElementsNodesHeight, externalAppsNodeHeight) -
        EXTERNAL_APPS_NODE_TEXT_LABEL_HEIGHT / 2, // offset for the text label
    },
  })

  elements.forEach((scenarioElement, i) => {
    const offset = i * (VERTICAL_GAP + SCENARIO_ELEMENT_NODE_HEIGHT)

    const id = scenarioElement.id

    nodes.push({
      id,
      type: ScenarioDiagramNodeType.ScenarioElement,
      position: { x: 0, y: offset },
      width: SCENARIO_ELEMENT_NODE_WIDTH,
      height: SCENARIO_ELEMENT_NODE_HEIGHT,
      data: scenarioElement,
    })

    edges.push(createEdge(externalAppsNodeId, id))
    edges.push(createEdge(id, yourAppNodeId))
  })

  return { nodes, edges }
}

function createEdge(source: string, target: string): Edge {
  const edge: Edge = {
    type: 'custom',

    id: `${source}-${target}`,
    source,
    target,

    style: {
      stroke: '#DDDDDD',
    },
  }

  return edge
}
